import {
  EListFilter,
  TCommunitiesListProps,
  TListHeaderProps,
  TMapSidebarCommunitiesListProps,
} from './MapSidebarCommunitiesList.types';
import { EModalAssetsManagerView } from '../../../ModalAssetsManager/ModalAssetsManager.types';
import { FRONTEND_DATE_FORMATS, UTCMoment } from 'src/utils/UTCMoment';
import {
  ListCommunityInfoQuery,
  useDeleteProjectMutation,
  useListCommunityInfoLazyQuery,
  useListPersonalCommunityInfoLazyQuery,
} from 'src/graphql';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { closeModal, openModal } from 'src/redux/modals/modals.slice';
import {
  selectCommunitiesCount,
  selectCommunitiesList,
} from 'src/redux/communities/communities.selectors';
import { selectIsLoggedIn, selectTokenAuth } from 'src/redux/auth/auth.selectors';

import { ApplicationContext } from 'src/contexts/ApplicationContext';
import { BaseSearch } from 'src/components/BaseSearch';
import { CommunityListItem } from 'src/components/CommunityListItem';
import { ConfigurationManagementCentral } from 'src/components/ConfigurationManagementCentral';
import { ContextMenu } from 'src/components/ContextMenu';
import { EAppMode } from 'src/utils/appMode';
import { EModalSize } from 'src/constants/modals';
import { FixedSizeList } from 'react-window';
import { LoadingWrapper } from 'src/components/LoadingWrapper';
import classnames from 'classnames';
import { convertLngLat } from 'src/utils/worldMap/helpers';
import { debounce } from 'lodash';
import { formatter } from 'src/utils/formatter';
import { getConfigurationLocation } from 'src/utils/configuration/getConfigurationLocation';
import { routesConfig } from 'src/routes/routes.config';
import s from './MapSidebarCommunitiesList.module.scss';
import { selectAppMode } from 'src/redux/application/application.selectors';
import { selectIsModalOpened } from 'src/redux/modals/modals.selectors';
import { setCommunitiesList } from 'src/redux/communities/communities.slice';
import { setActiveConfigurationUuid } from 'src/redux/configuration/configuration.slice';
import { setModalCommunitySummaryView } from 'src/redux/application/application.slice';
import { useAppDispatch } from 'src/redux/store';
import { useHistory } from 'react-router';
import { useModal } from 'src/hooks/useModal';
import { useSelector } from 'react-redux';
import vars from 'src/assets/styles/utils/vars.module.scss';

const FILTERS = [
  { name: 'All', value: EListFilter.All },
  { name: 'My Communities', value: EListFilter.MyCommunities },
];

const Header: React.FC = React.memo(() => {
  const communitiesCount = useSelector(selectCommunitiesCount);

  return (
    <div className={s.header}>
      <h2>{formatter.prefixWithZeros(communitiesCount)}_</h2>
      <h3>Simulations worldwide</h3>
    </div>
  );
});

const ListHeader: React.FC<TListHeaderProps> = ({
  activeFilter,
  onFilterChange,
  searchQuery,
  onSearchQueryChange,
  isLoggedIn,
}) => {
  return (
    <div className={s.listHeader}>
      {isLoggedIn && (
        <nav>
          <ul>
            {FILTERS.map((item) => (
              <li
                key={item.value}
                className={classnames({
                  [s.active]: activeFilter === item.value,
                })}>
                <button type="button" onClick={() => onFilterChange(item.value)}>
                  {item.name}
                </button>
              </li>
            ))}
          </ul>
        </nav>
      )}
      <BaseSearch
        name="communitiesSearch"
        theme="filled-gray"
        value={searchQuery}
        onChange={({ value }) => onSearchQueryChange(value)}
        label="Search by name"
        iconRight="search"
        iconRightColor={vars['color-black']}
      />
    </div>
  );
};

const ListItem = ({ index, style, data }) => {
  const item = data.items[index];
  const { timestamp, configurations } = item;
  const location = getConfigurationLocation(item);

  if (!location) return null;

  const locationCoords = convertLngLat(location);

  return (
    <div className={s.listItem} style={style}>
      <CommunityListItem
        className={s.listItem}
        user={item.user}
        configurationUuid={configurations[0].uuid}
        title={configurations[0].name}
        key={configurations[0].uuid}
        isPrivate={item.private}
        description={
          timestamp
            ? `Updated on ${UTCMoment.fromBackend(timestamp).format(
                FRONTEND_DATE_FORMATS.COMMUNITY_DATE,
              )}`
            : ''
        }
        locationCoords={locationCoords}
        onMenuClick={(menuTriggerRef, { communityUuid }) =>
          data.onMenuClick(menuTriggerRef, { communityUuid, uuid: item.uuid })
        }
      />
    </div>
  );
};

const CommunitiesList: React.FC<TCommunitiesListProps> = ({
  height,
  items,
  removeCommunity,
  updatePersonalCommunitiesList,
}) => {
  const history = useHistory();
  const { id: modalId } = useModal();
  const dispatch = useAppDispatch();
  const modalIsOpen = useSelector(selectIsModalOpened(modalId));
  const appMode = useSelector(selectAppMode);
  const [menuModalTriggerRef, setMenuModalTriggerRef] = useState<
    React.RefObject<HTMLButtonElement | null>
  >({
    current: null,
  });
  const [openedProjectUuid, setOpenedProjectUuid] = useState<string | null>(null);
  const [openedModalConfigUuid, setOpenedModalConfigUuid] = useState<string>();
  const [deleteProject] = useDeleteProjectMutation({ errorPolicy: 'all' });
  const { triggerCommunityDeleteAlert } = useContext(ApplicationContext);

  const closeActiveModal = () => {
    setOpenedProjectUuid(null);
    setMenuModalTriggerRef({ current: null });
    dispatch(closeModal(modalId));
  };

  const onMenuClick = (menuTriggerRef, { communityUuid, uuid }) => {
    setOpenedModalConfigUuid(communityUuid);
    if (modalIsOpen) {
      closeActiveModal();
    } else {
      setOpenedProjectUuid(uuid);
      setMenuModalTriggerRef({ current: menuTriggerRef });
      dispatch(openModal(modalId));
    }
  };

  const dropDownItems = [
    {
      title: 'Edit',
      icon: 'pencil-create' as const,
      onClick: () => {
        if (openedModalConfigUuid) {
          dispatch(setActiveConfigurationUuid(openedModalConfigUuid));
          dispatch(setModalCommunitySummaryView(EModalAssetsManagerView.Form));
        }
      },
    },
    {
      title: 'Delete',
      icon: 'custom-trash' as const,
      onClick: async () => {
        if (openedProjectUuid) {
          await triggerCommunityDeleteAlert();

          await removeCommunity({ projectUuid: openedProjectUuid });
          updatePersonalCommunitiesList();

          closeActiveModal();
        }
      },
    },
  ];

  return (
    <div className={s.communitiesList}>
      <FixedSizeList
        height={height}
        itemData={{
          onMenuClick,
          items,
        }}
        itemCount={items.length}
        itemSize={appMode === EAppMode.Desktop ? 90 : 215}
        onScroll={closeActiveModal}
        width={EModalSize.L370}
        className={classnames(s.communitiesListWindow)}>
        {ListItem}
      </FixedSizeList>
      <ContextMenu
        relativeElement={menuModalTriggerRef}
        modalId={modalId}
        position={{
          side: 'right',
          anchorPosition: 50,
        }}
        items={dropDownItems}
      />
    </div>
  );
};

export const MapSidebarList: React.FC<{
  mobileListClassName?: string;
  searchQuery;
  setSearchQuery;
}> = ({ mobileListClassName, searchQuery, setSearchQuery }) => {
  const dispatch = useAppDispatch();
  const [activeFilter, setActiveFilter] = useState(EListFilter.All);
  const [listHeight, setListHeight] = useState<number>();
  const communitiesList = useSelector(selectCommunitiesList);
  const token = useSelector(selectTokenAuth);
  const appMode = useSelector(selectAppMode);
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const [debouncedSearchQuery, __setDebouncedSearchQuery] = useState(searchQuery);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setDebouncedSearchQuery = useCallback(
    debounce((val) => {
      __setDebouncedSearchQuery(val);
    }, 400),
    [],
  );

  useEffect(() => {
    setDebouncedSearchQuery(searchQuery);
  }, [searchQuery, setDebouncedSearchQuery]);

  useEffect(() => {
    if (!isLoggedIn) {
      setActiveFilter(EListFilter.All);
    }
  }, [isLoggedIn]);

  const onListContainerLayout = useCallback(
    (elem: HTMLDivElement) => {
      if (elem && !listHeight) {
        setListHeight(elem.clientHeight);
      }
    },
    [listHeight],
  );

  const [
    fetchPersonalCommunities,
    { data: personalCommunities, loading: personalCommunitiesLoading },
  ] = useListPersonalCommunityInfoLazyQuery({ fetchPolicy: 'cache-and-network' });

  const [
    fetchCommunities,
    { data: allCommunities, loading: communitiesLoading },
  ] = useListCommunityInfoLazyQuery({ fetchPolicy: 'cache-and-network' });

  useEffect(() => {
    fetchCommunities();
    if (isLoggedIn) {
      fetchPersonalCommunities(); // Preload personal communities asynchronously right away if user is loggen in for better user experience
    }
  }, [fetchCommunities, fetchPersonalCommunities, isLoggedIn]);

  const projects = useMemo(() => {
    if (activeFilter === EListFilter.MyCommunities || appMode === EAppMode.Mobile) {
      return personalCommunities?.listPersonalCommunityInfo?.projects || [];
    } else {
      return allCommunities?.listCommunityInfo?.projects || [];
    }
  }, [activeFilter, allCommunities, appMode, personalCommunities]);

  useEffect(() => {
    type TProjects = NonNullable<
      NonNullable<ListCommunityInfoQuery['listCommunityInfo']>['projects']
    >;
    const items = projects
      .reduce((acc: TProjects, item) => {
        let include = false;

        if (!item) return acc;

        const communityHasLocation = Boolean(getConfigurationLocation(item));

        if (item.configurations?.length && communityHasLocation) {
          if (activeFilter === EListFilter.MyCommunities) {
            if (token?.username === item.user) {
              include = true;
            }
          } else {
            include = true;
          }
        }

        if (include) {
          acc.push({
            ...item,
            configurations:
              item.configurations && item.configurations[0] ? [item.configurations[0]] : [],
          });
        }

        return acc;
      }, [])
      .filter((item) => {
        return item!.name!.toLowerCase().includes(debouncedSearchQuery.toLowerCase());
      });

    items.sort((a, b) => +new Date(b?.timestamp) - +new Date(a?.timestamp));

    dispatch(setCommunitiesList(items));
  }, [activeFilter, dispatch, projects, debouncedSearchQuery, token?.username]);

  if (appMode === EAppMode.Mobile) {
    return (
      <div className={classnames(s.list, mobileListClassName)} ref={onListContainerLayout}>
        <LoadingWrapper loading={!listHeight || personalCommunitiesLoading || communitiesLoading}>
          {() => (
            <ConfigurationManagementCentral>
              {({ handleCommunityRemove }) => (
                <CommunitiesList
                  items={communitiesList}
                  height={listHeight!}
                  removeCommunity={handleCommunityRemove}
                  updatePersonalCommunitiesList={fetchPersonalCommunities}
                />
              )}
            </ConfigurationManagementCentral>
          )}
        </LoadingWrapper>
      </div>
    );
  }

  return (
    <div className={s.listContainer}>
      <ListHeader
        activeFilter={activeFilter}
        onFilterChange={setActiveFilter}
        searchQuery={searchQuery}
        onSearchQueryChange={setSearchQuery}
        isLoggedIn={isLoggedIn}
      />

      <div className={s.list} ref={onListContainerLayout}>
        <LoadingWrapper loading={!listHeight || personalCommunitiesLoading || communitiesLoading}>
          {() => (
            <ConfigurationManagementCentral>
              {({ handleCommunityRemove }) => (
                <CommunitiesList
                  items={communitiesList}
                  height={listHeight!}
                  removeCommunity={handleCommunityRemove}
                  updatePersonalCommunitiesList={fetchPersonalCommunities}
                />
              )}
            </ConfigurationManagementCentral>
          )}
        </LoadingWrapper>
      </div>
    </div>
  );
};

export const MapSidebarCommunitiesList: React.FC<TMapSidebarCommunitiesListProps> = () => {
  const [searchQuery, setSearchQuery] = useState('');
  return (
    <div className={s.container}>
      <Header />
      <MapSidebarList searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
    </div>
  );
};
